import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  IconButton,
  DialogContent,
  Dialog,
  Chip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  useGetCustomerReportQuery,
  useGetResellerCustomersQuery,
  useGetResellerDeliveryStatusCountQuery,
  useSearchResellerNumberCampaignsQuery,
  useGetclickercustmerSmsreportQuery,
  useGetclickersmstotalreportQuery,
} from "../store/services/reportApi";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { layoutCSS } from "../component/common/commonStyle";
import { useGetrcsresellercampaignlistQuery } from "../store/services/rcstemplateApi";

// ── Unified styles ────────────────────────────────────────────────────────────

const cellSX = {
  fontSize: "0.75rem",
  padding: "6px 8px",
  textAlign: "center",
  borderBottom: "1px solid #000",
  borderRight: "1px solid #000",
  "&:last-child": { borderRight: "none" },
};

const tableHeadSX = {
  bgcolor: "#0288d1",
  "& th": {
    fontWeight: "bold",
    color: "white",
    fontSize: "0.7rem",
    textTransform: "uppercase",
    borderBottom: "1px solid #000",
    borderRight: "1px solid #000",
    padding: "8px",
    textAlign: "center",
    "&:last-child": { borderRight: "none" },
  },
};

const tableContainerSX = {
  mt: 3,
  border: "1px solid #000",
  borderRadius: "4px",
  overflow: "hidden",
};

const tableSX = {
  borderCollapse: "collapse",
};

const datePickerSx = {
  borderRadius: "0.5rem",
  minWidth: 100,
  width: 140,
  "& .MuiOutlinedInput-root": {
    background: "rgba(255,255,255,0.7)",
    "& fieldset": { borderColor: "#E2E8F0" },
    "&:hover fieldset": { borderColor: "#94A3B8" },
  },
  "& .MuiInputLabel-root": { color: "#475569" },
  "& .Mui-disabled": {
    "& .MuiOutlinedInput-input": {
      color: "#334155 !important",
      WebkitTextFillColor: "#334155 !important",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
  },
};

// ─────────────────────────────────────────────────────────────────────────────

const CustomerSmsReport = () => {
  const [startdate, setStartDate] = useState(dayjs());
  const [enddate, setEndDate] = useState(dayjs());
  const [globalSearch, setGlobalSearch] = useState("");
  const [reportType, setReportType] = useState("sms");
  const [searchPage, setSearchPage] = useState(1);
  const [customerNameSearch, setCustomerNameSearch] = useState("");
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data: customerList } = useGetResellerCustomersQuery();
  const [openSmsDialog, setOpenSmsDialog] = useState(false);
  const [selectedSmsText, setSelectedSmsText] = useState("");
  const [selectedSmsCampaignName, setSelectedSmsCampaignName] = useState("");
  const selectedCustomerId = customerList?.data?.find(
    (cust) =>
      cust?.customer_profile?.customer_name?.toLowerCase().trim() ===
      customerNameSearch.toLowerCase().trim(),
  )?.customer_id;
  const [searchNumber, setSearchNumber] = useState("");

  const {
    data: smsData,
    isLoading: smsLoading,
    error: smsError,
  } = useGetCustomerReportQuery(
    reportType === "sms"
      ? {
          startdate: startdate.format("YYYY-MM-DD"),
          enddate: enddate.format("YYYY-MM-DD"),
          page,
          limit: 25,
          username: customerNameSearch.trim(),
        }
      : {},
    { skip: reportType !== "sms" },
  );

  const handleOpenDetails = (campaignId) => {
    navigate(`/reseller-rcs-campaign-details/${campaignId}`);
  };

  const { data: resellerSummary } = useGetResellerDeliveryStatusCountQuery({
    startdate: startdate.format("YYYY-MM-DD"),
    enddate: enddate.format("YYYY-MM-DD"),
  });

  // ── RCS list (new query) ──
  const {
    data: rcsData,
    isLoading: rcsLoading,
    error: rcsError,
  } = useGetrcsresellercampaignlistQuery(
    {
      startdate: startdate.format("YYYY-MM-DD"),
      enddate: enddate.format("YYYY-MM-DD"),
      page,
      limit: 25,
    },
    { skip: reportType !== "rcs" },
  );

  const {
    data: clickerData,
    isLoading: clickerLoading,
    error: clickerError,
  } = useGetclickercustmerSmsreportQuery(
    reportType === "clicker"
      ? {
          startdate: startdate.format("YYYY-MM-DD"),
          enddate: enddate.format("YYYY-MM-DD"),
        }
      : {},
    { skip: reportType !== "clicker" },
  );

  const { data: clickerTotals } = useGetclickersmstotalreportQuery(
    reportType === "clicker"
      ? {
          startdate: startdate.format("YYYY-MM-DD"),
          enddate: enddate.format("YYYY-MM-DD"),
        }
      : {},
    { skip: reportType !== "clicker" },
  );

  const rcsList = rcsData?.data || [];
  const summary = rcsList.reduce(
    (acc, item) => {
      const o = item.rcs_overview || {};
      acc.total += o.total || 0;
      acc.sent += o.sent || 0;
      acc.delivered += o.delivered || 0;
      acc.read += o.read || 0;
      acc.failed += o.failed || 0;
      acc.pending += o.pending || 0;
      acc.rejected += o.rejected || 0;
      acc.disabled += o.disabled || 0;
      acc.limitExhausted += o.limitExhausted || 0;
      return acc;
    },
    {
      total: 0,
      sent: 0,
      delivered: 0,
      read: 0,
      failed: 0,
      pending: 0,
      rejected: 0,
      disabled: 0,
      limitExhausted: 0,
    },
  );

  const {
    data: globalSearchData,
    isLoading: globalSearchLoading,
    isError: globalSearchError,
  } = useSearchResellerNumberCampaignsQuery(
    {
      ph_no: globalSearch,
      startDate: startdate?.format("YYYY-MM-DD"),
      endDate: enddate?.format("YYYY-MM-DD"),
      page: searchPage,
    },
    { skip: searchNumber.length !== 10 },
  );

  const handleOpenDialog = (campaign, status = "") => {
    navigate(`/numbersbycampaign/${campaign.campaign_id}/${status || "All"}`);
  };

  const handleSearchPageChange = (_, value) => {
    setSearchPage(value);
  };

  const handleOpenSmsDialog = (smsText, campaignName) => {
    setSelectedSmsText(smsText);
    setSelectedSmsCampaignName(campaignName);
    setOpenSmsDialog(true);
  };

  const handleCloseSmsDialog = () => {
    setOpenSmsDialog(false);
    setSelectedSmsText("");
    setSelectedSmsCampaignName("");
  };

  return (
    <Box sx={layoutCSS}>
      <Typography
        variant="h6"
        sx={{ mb: 1, fontSize: "0.9rem", fontWeight: "bold" }}
      >
        Customer Sent Report
      </Typography>

      {/* ── Filter bar ── */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
          elevation: 2,
          borderRadius: "0.5rem",
          padding: "1rem",
          backgroundColor: "#ffffff52",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 12px rgba(14,165,233,0.1)",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start Date"
            value={startdate}
            onChange={(newValue) => {
              setStartDate(newValue);
              setPage(1);
              if (
                enddate &&
                newValue &&
                enddate.diff(newValue, "month", true) > 1
              ) {
                setEndDate(newValue.add(1, "month"));
              }
            }}
            maxDate={dayjs()}
            format="DD/MM/YYYY"
            sx={datePickerSx}
            slotProps={{ textField: { size: "small" } }}
          />
        </LocalizationProvider>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="End Date"
            value={enddate}
            onChange={(newValue) => {
              setEndDate(newValue);
              setPage(1);
            }}
            maxDate={
              startdate
                ? startdate.add(1, "month").isBefore(dayjs())
                  ? startdate.add(1, "month")
                  : dayjs()
                : dayjs()
            }
            minDate={startdate}
            format="DD/MM/YYYY"
            sx={datePickerSx}
            slotProps={{ textField: { size: "small" } }}
          />
        </LocalizationProvider>

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel id="report-type-label" sx={{ fontSize: "0.75rem" }}>
            Report Type
          </InputLabel>
          <Select
            labelId="report-type-label"
            value={reportType}
            label="Report Type"
            onChange={(e) => {
              setReportType(e.target.value);
              setPage(1);
            }}
            sx={{
              fontSize: "0.75rem",
              minHeight: "30px",
              ".MuiSelect-select": {
                padding: "6px 12px",
                display: "flex",
                alignItems: "center",
              },
            }}
          >
            <MenuItem value="sms" sx={{ fontSize: "0.75rem" }}>SMS</MenuItem>
            <MenuItem value="clicker" sx={{ fontSize: "0.75rem" }}>Clicker</MenuItem>
            <MenuItem value="rcs" sx={{ fontSize: "0.75rem" }}>RCS</MenuItem>
            <MenuItem value="whatsapp" sx={{ fontSize: "0.75rem" }}>WhatsApp</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Customer Name"
          value={customerNameSearch}
          onChange={(e) => setCustomerNameSearch(e.target.value)}
          size="small"
          sx={{
            width: 120,
            "& .MuiInputBase-input": { fontSize: "0.75rem", padding: "6px 8px" },
            "& .MuiInputLabel-root": { fontSize: "0.75rem" },
          }}
        />

        <TextField
          label="Search Phone Number"
          value={globalSearch}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, "");
            setGlobalSearch(value);
            setSearchPage(1);
            if (value.length === 10) setSearchNumber(value);
            else setSearchNumber("");
          }}
          size="small"
          sx={{
            width: 130,
            "& .MuiInputBase-input": { fontSize: "0.75rem", padding: "6px 8px" },
            "& .MuiInputLabel-root": { fontSize: "0.75rem" },
          }}
        />
      </Box>

      {/* ── SMS summary ── */}
      {reportType === "sms" && (
        <Box sx={{ mt: 1.5, mb: 1 }}>
          <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
            TOTAL : {resellerSummary?.data?.totals?.total || 0} | DELIVERED :{" "}
            {resellerSummary?.data?.totals?.delivered || 0} | FAILED :{" "}
            {resellerSummary?.data?.totals?.undelivered || 0} | PENDING :{" "}
            {resellerSummary?.data?.totals?.pending || 0}
          </Typography>
        </Box>
      )}

      {/* ── RCS summary ── */}
      {reportType === "rcs" && (
        <Typography sx={{ fontSize: "12px", fontWeight: 600, mt: 1 }}>
          TOTAL: {summary.total} | SENT: {summary.sent} | DELIVRD:{" "}
          {summary.delivered} | FAILED: {summary.failed} | READ: {summary.read}{" "}
          | PENDING: {summary.pending} | REJECTED: {summary.rejected} |
          DISABLED: {summary.disabled} | LIMIT EXHAUSTED:{" "}
          {summary.limitExhausted}
        </Typography>
      )}

      {/* ── Clicker summary ── */}
      {reportType === "clicker" && (
        <Typography sx={{ fontSize: "12px", fontWeight: 600, mt: 1, mb: 1 }}>
          TOTAL : {clickerTotals?.data?.totals?.total || 0} | DELIVRD :{" "}
          {clickerTotals?.data?.totals?.delivered || 0} | FAILED :{" "}
          {clickerTotals?.data?.totals?.undelivered || 0} | PENDING :{" "}
          {clickerTotals?.data?.totals?.pending || 0}
        </Typography>
      )}

      {/* ── Phone number global search table ── */}
      {searchNumber.length === 10 && (
        <>
          <TableContainer component={Paper} sx={tableContainerSX}>
            <Table sx={tableSX}>
              <TableHead sx={tableHeadSX}>
                <TableRow>
                  <TableCell>Created At</TableCell>
                  <TableCell>Campaign ID</TableCell>
                  <TableCell>Phone Number</TableCell>
                  <TableCell>Sender ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Error Code</TableCell>
                  <TableCell>Fid</TableCell>
                  <TableCell>SMS Text</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {globalSearchLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={cellSX}>Loading...</TableCell>
                  </TableRow>
                ) : globalSearchError || globalSearchData?.status === false ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={cellSX}>No matching number found</TableCell>
                  </TableRow>
                ) : (
                  globalSearchData?.data?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell sx={cellSX}>{dayjs(item.createdat).format("DD/MM/YYYY HH:mm")}</TableCell>
                      <TableCell sx={cellSX}>{item.campaign_id}</TableCell>
                      <TableCell sx={cellSX}>{item.ph_no}</TableCell>
                      <TableCell sx={cellSX}>{item.sender_id}</TableCell>
                      <TableCell sx={cellSX}>{item.stat}</TableCell>
                      <TableCell sx={cellSX}>{item.errcode}</TableCell>
                      <TableCell sx={cellSX}>{item.fid}</TableCell>
                      <TableCell sx={cellSX}>{item.totalLogs?.smstext || "N/A"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {globalSearchData?.pagination?.totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={globalSearchData?.pagination?.totalPages || 1}
                page={searchPage}
                onChange={handleSearchPageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* ── SMS table ── */}
      {!globalSearch && reportType === "sms" && (
        <TableContainer component={Paper} sx={tableContainerSX}>
          <Table sx={tableSX}>
            <TableHead sx={tableHeadSX}>
              <TableRow>
                <TableCell sx={{ width: 100, maxWidth: 100 }}>Date</TableCell>
                <TableCell sx={{ width: 100, maxWidth: 100 }}>Campaign ID</TableCell>
                <TableCell sx={{ width: 100, maxWidth: 100 }}>User Name</TableCell>
                <TableCell sx={{ width: 70, maxWidth: 70 }}>Total Numbers</TableCell>
                <TableCell sx={{ width: 60, maxWidth: 60 }}>Msg Count</TableCell>
                <TableCell sx={{ width: 60, maxWidth: 60 }}>Total Count</TableCell>
                <TableCell>Template Name</TableCell>
                <TableCell>SenderID</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {smsLoading ? (
                <TableRow><TableCell colSpan={9} align="center" sx={cellSX}>Loading...</TableCell></TableRow>
              ) : smsError ? (
                <TableRow><TableCell colSpan={9} align="center" sx={cellSX}>Error loading data</TableCell></TableRow>
              ) : smsData?.data?.length > 0 ? (
                smsData.data.map((campaign) => {
                  const totalcount = (campaign.total_numbers || 0) * (campaign.msgcount || 1);
                  return (
                    <TableRow key={campaign.campaign_id}>
                      <TableCell sx={cellSX}>{dayjs.utc(campaign.time_to_send).format("DD/MM/YYYY HH:mm")}</TableCell>
                      <TableCell sx={cellSX}>{campaign.campaign_id}</TableCell>
                      <TableCell sx={cellSX}>{campaign?.username || "N/A"}</TableCell>
                      <TableCell sx={cellSX}>
                        <Typography
                          color="primary"
                          sx={{ cursor: "pointer", fontSize: "0.8rem" }}
                          onClick={() => handleOpenDialog(campaign, "")}
                        >
                          {campaign.total_numbers}
                        </Typography>
                      </TableCell>
                      <TableCell sx={cellSX}>{campaign.msgcount}</TableCell>
                      <TableCell sx={cellSX}>{totalcount}</TableCell>
                      <TableCell sx={cellSX}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                          <Typography sx={{ fontSize: "0.8rem" }}>{campaign.template_name || "N/A"}</Typography>
                          {campaign.smstext && (
                            <IconButton size="small" onClick={() => handleOpenSmsDialog(campaign.smstext, campaign.template_name)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={cellSX}>{campaign.header_name}</TableCell>
                      <TableCell sx={cellSX}>{campaign.file_status}</TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow><TableCell colSpan={9} align="center" sx={cellSX}>No records found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          {smsData?.pagination?.totalPages > 1 && (
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} p={1}>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
                Total Records: {smsData?.pagination?.totalRecords || 0}
              </Typography>
              <Pagination
                count={smsData?.pagination?.totalPages || 1}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </TableContainer>
      )}

      {/* ── RCS table ── */}
      {!globalSearch && reportType === "rcs" && (
        <TableContainer component={Paper} sx={tableContainerSX}>
          <Table sx={tableSX}>
            <TableHead sx={tableHeadSX}>
              <TableRow>
                <TableCell>Sr No</TableCell>
                <TableCell>Campaign ID</TableCell>
                <TableCell>Campaign Name</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>User Name</TableCell>
                <TableCell>Bot Name</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Executed At</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Overview</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rcsLoading ? (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={cellSX}>Loading...</TableCell>
                </TableRow>
              ) : rcsError ? (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={cellSX}>Error loading data</TableCell>
                </TableRow>
              ) : rcsData?.data?.length > 0 ? (
                rcsData.data.map((campaign) => (
                  <TableRow key={campaign.campaign_id}>
                    <TableCell sx={cellSX}>{campaign.sr_no}</TableCell>
                    <TableCell sx={cellSX}>{campaign.campaign_id}</TableCell>
                    <TableCell sx={cellSX}>{campaign.campaign_name}</TableCell>
                    <TableCell sx={cellSX}>{campaign.customer_name || "N/A"}</TableCell>
                    <TableCell sx={cellSX}>{campaign.username || "N/A"}</TableCell>
                    <TableCell sx={cellSX}>{campaign.bot_name}</TableCell>
                    <TableCell sx={cellSX}>{campaign.template_name}</TableCell>
                    <TableCell sx={cellSX}>
                      {dayjs(campaign.created_at).format("DD/MM/YYYY HH:mm")}
                    </TableCell>
                    <TableCell sx={cellSX}>
                      {dayjs(campaign.executed_at).format("DD/MM/YYYY HH:mm")}
                    </TableCell>
                    <TableCell sx={cellSX}>{campaign.campaign_type}</TableCell>
                    <TableCell sx={cellSX}>
                      <Chip
                        label={campaign.file_status || campaign.status}
                        size="small"
                        color={campaign.file_status === "Completed" ? "success" : "default"}
                        sx={{ fontSize: "0.7rem" }}
                      />
                    </TableCell>
                    <TableCell sx={cellSX}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <SendIcon sx={{ fontSize: "0.85rem", color: "info.main" }} />
                          <Typography variant="caption" sx={{ fontSize: "0.75rem", color: "info.main" }}>
                            Sent: {campaign.rcs_overview?.sent ?? 0}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <CheckCircleIcon sx={{ fontSize: "0.85rem", color: "success.main" }} />
                          <Typography variant="caption" sx={{ fontSize: "0.75rem", color: "success.main" }}>
                            Delivered: {campaign.rcs_overview?.delivered ?? 0}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <VisibilityIcon sx={{ fontSize: "0.85rem", color: "primary.main" }} />
                          <Typography variant="caption" sx={{ fontSize: "0.75rem", color: "primary.main" }}>
                            Read: {campaign.rcs_overview?.read ?? 0}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography variant="caption" sx={{ fontSize: "0.75rem", color: "error.main" }}>
                            Failed: {campaign.rcs_overview?.failed ?? 0}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={cellSX}>
                      <IconButton size="small" onClick={() => handleOpenDetails(campaign.campaign_id)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={cellSX}>No records found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {rcsData?.pagination?.totalPages > 1 && (
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} p={1}>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
                Total Records: {rcsData?.pagination?.totalRecords || 0}
              </Typography>
              <Pagination
                count={rcsData?.pagination?.totalPages || 1}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </TableContainer>
      )}

      {/* ── Clicker table ── */}
      {!globalSearch && reportType === "clicker" && (
        <TableContainer component={Paper} sx={tableContainerSX}>
          <Table sx={tableSX}>
            <TableHead sx={tableHeadSX}>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Campaign ID</TableCell>
                <TableCell>Campaign Name</TableCell>
                <TableCell>User Name</TableCell>
                <TableCell>Total Numbers</TableCell>
                <TableCell>Template Name</TableCell>
                <TableCell>Sender ID</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clickerLoading ? (
                <TableRow><TableCell colSpan={8} align="center" sx={cellSX}>Loading...</TableCell></TableRow>
              ) : clickerError ? (
                <TableRow><TableCell colSpan={8} align="center" sx={cellSX}>Error loading data</TableCell></TableRow>
              ) : clickerData?.data?.length > 0 ? (
                clickerData.data.map((campaign) => (
                  <TableRow key={campaign.clicker_campaign_id}>
                    <TableCell sx={cellSX}>{dayjs.utc(campaign.time_to_send).format("DD/MM/YYYY HH:mm")}</TableCell>
                    <TableCell sx={cellSX}>{campaign.clicker_campaign_id}</TableCell>
                    <TableCell sx={cellSX}>{campaign.campaignname}</TableCell>
                    <TableCell sx={cellSX}>{campaign.username}</TableCell>
                    <TableCell sx={cellSX}>{campaign.total_numbers}</TableCell>
                    <TableCell sx={cellSX}>{campaign.template_name}</TableCell>
                    <TableCell sx={cellSX}>{campaign.header_name}</TableCell>
                    <TableCell sx={cellSX}>{campaign.file_status}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow><TableCell colSpan={8} align="center" sx={cellSX}>No records found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          {clickerData?.pagination?.totalPages > 1 && (
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} p={1}>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
                Total Records: {clickerData?.pagination?.totalRecords || 0}
              </Typography>
              <Pagination
                count={clickerData?.pagination?.totalPages || 1}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </TableContainer>
      )}

      {/* ── SMS text viewer dialog ── */}
      <Dialog open={openSmsDialog} onClose={handleCloseSmsDialog} maxWidth="sm" fullWidth>
        <DialogContent>
          <Typography sx={{ fontSize: "0.85rem", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {selectedSmsText || "No SMS text available"}
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CustomerSmsReport;